import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Receipt, AlertTriangle, CheckCircle2, Wallet, Loader2, MessageCircle, X } from 'lucide-react'
import api from '../api'
import useBranding from '../useBranding'

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }
function fmtTanggal(t) { return t ? new Date(t + 'T00:00:00').toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-' }

// Halaman Tagihan Publik (2026-07-22, fase 1 notifikasi isolir -- lihat catatan status ISOLIR di
// server/redamanScan.js: TIDAK PERNAH memutus internet, ini murni halaman info yg dibagikan MANUAL
// oleh admin/teknisi ke pelanggan yg nunggak, mirror pola halaman publik lain (TiketForm.jsx dst) --
// TANPA login, akses lewat token per-pelanggan (server/routes/public.js GET /tagihan/:token).
export default function TagihanPublik() {
  const { token } = useParams()
  const businessName = useBranding()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [bayarId, setBayarId] = useState(null) // id tagihan yg lagi diproses tombol Bayar
  const [bayarError, setBayarError] = useState('')
  const [showPopup, setShowPopup] = useState(false)

  useEffect(() => {
    api.get(`/public/tagihan/${token}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.message || 'Gagal memuat tagihan'))
      .finally(() => setLoading(false))
  }, [token])

  // Popup notice keterlambatan (2026-07-29, Isolir Otomatis Bagian C) -- muncul lagi setelah
  // interval yg admin atur (Pengaturan > Isolir Otomatis, default 6 jam), TIDAK setiap kali halaman
  // dibuka -- supaya tidak mengganggu pelanggan yg lagi mau bayar/refresh halaman berkali-kali.
  // Timestamp dismiss disimpan per-token di localStorage (murni sisi klien, tidak perlu server).
  useEffect(() => {
    if (!data || data.tagihan_belum_lunas.length === 0) return
    const key = `tagihanPopupDismissedAt:${token}`
    const lastDismissed = parseInt(localStorage.getItem(key), 10) || 0
    const intervalMs = (data.popup_interval_jam || 6) * 3600000
    if (Date.now() - lastDismissed > intervalMs) setShowPopup(true)
  }, [data, token])

  function tutupPopup() {
    localStorage.setItem(`tagihanPopupDismissedAt:${token}`, String(Date.now()))
    setShowPopup(false)
  }

  // Fase 3 "Bayar Online" (2026-07-26) -- redirect PENUH ke halaman pembayaran Midtrans (bukan
  // Snap.js popup), server yg bikin transaksinya (lihat server/routes/public.js POST /tagihan/:token/bayar).
  // Metode yg tampil di halaman Midtrans ikut "Payment Methods" aktif di dashboard Midtrans admin,
  // BUKAN daftar tetap di sini (awalnya cuma QRIS, diperluas 2026-07-26 sesuai permintaan).
  async function bayarOnline(t) {
    setBayarError('')
    setBayarId(t.id)
    try {
      const res = await api.post(`/public/tagihan/${token}/bayar`, { tagihan_id: t.id })
      window.location.href = res.data.redirect_url
    } catch (e) {
      setBayarError(e.response?.data?.message || 'Gagal membuat transaksi pembayaran')
      setBayarId(null)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '20px 16px 40px' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <Receipt size={22} color="var(--brand)" />
          <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>{businessName} — Tagihan Saya</h1>
        </div>

        {loading && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 30 }}>Memuat...</p>}

        {error && (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <AlertTriangle size={28} color="var(--danger)" style={{ marginBottom: 8 }} />
            <p style={{ color: 'var(--text)', fontWeight: 600 }}>{error}</p>
          </div>
        )}

        {data && (
          <>
            <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 18 }}>
              Halo <strong>{data.nama}</strong>, berikut rincian tagihan Anda yang belum lunas.
            </p>

            {data.tagihan_belum_lunas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--success)' }}>
                <CheckCircle2 size={36} strokeWidth={1.5} style={{ marginBottom: 10 }} />
                <p style={{ fontWeight: 700, fontSize: 15 }}>Semua tagihan sudah lunas</p>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Terima kasih atas pembayarannya.</p>
              </div>
            ) : (
              data.tagihan_belum_lunas.map(t => (
                <div key={t.periode} style={{
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
                  padding: 16, marginBottom: 12
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Periode {t.periode}</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 2 }}>{t.nama_paket}</div>
                  {t.diskon > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-muted)' }}>Harga Paket</span>
                        <span>{rp(t.harga_bulanan)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 13, gap: 12 }}>
                        <span style={{ color: 'var(--success)' }}>🎁 {t.catatan_diskon || 'Diskon'}</span>
                        <span style={{ color: 'var(--success)', whiteSpace: 'nowrap' }}>−{rp(t.diskon)}</span>
                      </div>
                      {t.biaya_lain > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 13, gap: 12 }}>
                          <span style={{ color: 'var(--text-muted)' }}>{t.catatan_biaya_lain || 'Biaya lain'}</span>
                          <span style={{ whiteSpace: 'nowrap' }}>{rp(t.biaya_lain)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: t.diskon > 0 ? 4 : 10, fontSize: 13 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Total Tagihan</span>
                    <span>{rp(t.total_tagihan)}</span>
                  </div>
                  {t.total_dibayar > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 13 }}>
                      <span style={{ color: 'var(--text-muted)' }}>Sudah Dibayar</span>
                      <span style={{ color: 'var(--success)' }}>{rp(t.total_dibayar)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontWeight: 700 }}>
                    <span>Sisa Tagihan</span>
                    <span style={{ color: 'var(--danger)' }}>{rp(t.sisa)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 6 }}>Jatuh tempo: {fmtTanggal(t.jatuh_tempo)}</div>
                  <button
                    onClick={() => bayarOnline(t)}
                    disabled={bayarId === t.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      width: '100%', marginTop: 12, padding: '10px 0', borderRadius: 8, border: 'none',
                      background: 'var(--brand)', color: '#fff', fontWeight: 700, fontSize: 13.5,
                      cursor: bayarId === t.id ? 'default' : 'pointer', opacity: bayarId === t.id ? 0.7 : 1
                    }}
                  >
                    {bayarId === t.id ? <Loader2 size={16} className="spin" /> : <Wallet size={16} />}
                    Bayar Sekarang
                  </button>
                </div>
              ))
            )}

            {bayarError && (
              <p style={{ fontSize: 12.5, color: 'var(--danger)', textAlign: 'center', marginTop: 4, marginBottom: 12 }}>{bayarError}</p>
            )}

            <p style={{ fontSize: 11.5, color: 'var(--text-faint)', textAlign: 'center', marginTop: 20 }}>
              Sudah membayar? Hubungi admin/teknisi kami agar segera dicatat.
            </p>
          </>
        )}
      </div>

      {showPopup && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 14, padding: 22, maxWidth: 380, width: '100%',
            border: '1px solid var(--border)', boxShadow: '0 12px 32px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <MessageCircle size={22} color="var(--brand)" />
              <button
                onClick={tutupPopup}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
                aria-label="Tutup"
              >
                <X size={18} />
              </button>
            </div>
            <p style={{ fontWeight: 700, fontSize: 15, marginTop: 10, marginBottom: 6, color: 'var(--text)' }}>
              {data.popup_judul}
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 18, whiteSpace: 'pre-wrap' }}>
              {data.popup_pesan}
            </p>
            <button
              onClick={tutupPopup}
              style={{
                width: '100%', padding: '10px 0', borderRadius: 8, border: 'none',
                background: 'var(--brand)', color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer'
              }}
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
